import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, PlayIcon, SaveIcon, DownloadIcon, UploadIcon, ZoomInIcon, ZoomOutIcon, RotateCwIcon, CheckCircleIcon, XCircleIcon, ClockIcon, FileTextIcon, LayersIcon, BoxIcon, ArrowRightIcon, UserIcon, UsersIcon, BellIcon, AlertCircleIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
// Mock data types
type WorkflowDefinition = {
  id: string;
  name: string;
  description: string;
  documentType: string;
  version: string;
  status: 'Active' | 'Draft' | 'Inactive';
  bpmnXml: string;
  createdAt: string;
  updatedAt: string;
  lastPublishedAt?: string;
  isDefault: boolean;
};
type DocumentType = {
  id: string;
  name: string;
  description: string;
  category: string;
};
export function WorkflowManager({
  locale
}) {
  const [activeView, setActiveView] = useState<'list' | 'edit' | 'preview'>('list');
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDefinition | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDocumentType, setFilterDocumentType] = useState<string>('all');
  // Mock data for document types
  const documentTypes: DocumentType[] = [{
    id: 'invoice',
    name: 'Invoice',
    description: 'Accounts payable invoice',
    category: 'AP'
  }, {
    id: 'po',
    name: 'Purchase Order',
    description: 'Procurement purchase order',
    category: 'Procurement'
  }, {
    id: 'requisition',
    name: 'Requisition',
    description: 'Purchase requisition',
    category: 'Procurement'
  }, {
    id: 'vendor',
    name: 'Vendor',
    description: 'Vendor record',
    category: 'Vendor Management'
  }, {
    id: 'credit-note',
    name: 'Credit Note',
    description: 'AP credit note',
    category: 'AP'
  }, {
    id: 'receipt',
    name: 'Goods Receipt',
    description: 'Goods receipt document',
    category: 'Procurement'
  }, {
    id: 'expense',
    name: 'Expense Claim',
    description: 'Employee expense claim',
    category: 'Expenses'
  }, {
    id: 'contract',
    name: 'Contract',
    description: 'Vendor contract',
    category: 'Contract Management'
  }];
  // Mock data for workflows
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([{
    id: 'wf-001',
    name: 'Invoice Approval Workflow',
    description: 'Standard invoice approval process with delegation thresholds',
    documentType: 'invoice',
    version: '1.2',
    status: 'Active',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2023-03-10T14:30:00Z',
    lastPublishedAt: '2023-03-10T14:30:00Z',
    isDefault: true
  }, {
    id: 'wf-002',
    name: 'Purchase Order Approval',
    description: 'Approval workflow for purchase orders with budget validation',
    documentType: 'po',
    version: '1.0',
    status: 'Active',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-02-01T09:15:00Z',
    updatedAt: '2023-02-15T11:20:00Z',
    lastPublishedAt: '2023-02-15T11:20:00Z',
    isDefault: true
  }, {
    id: 'wf-003',
    name: 'Vendor Onboarding',
    description: 'Multi-step vendor onboarding process with compliance checks',
    documentType: 'vendor',
    version: '2.1',
    status: 'Active',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2022-11-10T08:45:00Z',
    updatedAt: '2023-04-05T16:10:00Z',
    lastPublishedAt: '2023-04-05T16:10:00Z',
    isDefault: true
  }, {
    id: 'wf-004',
    name: 'High Value Invoice Approval',
    description: 'Special approval process for high value invoices',
    documentType: 'invoice',
    version: '1.0',
    status: 'Draft',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-04-20T13:30:00Z',
    updatedAt: '2023-04-20T13:30:00Z',
    isDefault: false
  }, {
    id: 'wf-005',
    name: 'Contract Approval',
    description: 'Legal and financial approval workflow for contracts',
    documentType: 'contract',
    version: '1.1',
    status: 'Active',
    bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
    createdAt: '2023-03-01T10:20:00Z',
    updatedAt: '2023-05-12T09:45:00Z',
    lastPublishedAt: '2023-05-12T09:45:00Z',
    isDefault: true
  }]);
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Handler for creating a new workflow
  const handleCreateWorkflow = () => {
    const newWorkflow: WorkflowDefinition = {
      id: `wf-${new Date().getTime()}`,
      name: 'New Workflow',
      description: '',
      documentType: '',
      version: '0.1',
      status: 'Draft',
      bpmnXml: '<?xml version="1.0" encoding="UTF-8"?>...',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDefault: false
    };
    setSelectedWorkflow(newWorkflow);
    setActiveView('edit');
  };
  // Handler for editing a workflow
  const handleEditWorkflow = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setActiveView('edit');
  };
  // Handler for previewing a workflow
  const handlePreviewWorkflow = (workflow: WorkflowDefinition) => {
    setSelectedWorkflow(workflow);
    setActiveView('preview');
    setZoomLevel(100); // Reset zoom level
  };
  // Handler for duplicating a workflow
  const handleDuplicateWorkflow = (workflow: WorkflowDefinition) => {
    const duplicatedWorkflow: WorkflowDefinition = {
      ...workflow,
      id: `wf-${new Date().getTime()}`,
      name: `Copy of ${workflow.name}`,
      status: 'Draft',
      version: '0.1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastPublishedAt: undefined,
      isDefault: false
    };
    setWorkflows([...workflows, duplicatedWorkflow]);
  };
  // Handler for activating/deactivating a workflow
  const handleToggleWorkflowStatus = (workflowId: string) => {
    setWorkflows(workflows.map(wf => wf.id === workflowId ? {
      ...wf,
      status: wf.status === 'Active' ? 'Inactive' : wf.status === 'Inactive' ? 'Active' : wf.status,
      updatedAt: new Date().toISOString(),
      ...(wf.status === 'Inactive' ? {
        lastPublishedAt: new Date().toISOString()
      } : {})
    } : wf));
  };
  // Handler for publishing a draft workflow
  const handlePublishWorkflow = (workflowId: string) => {
    setWorkflows(workflows.map(wf => wf.id === workflowId ? {
      ...wf,
      status: 'Active',
      updatedAt: new Date().toISOString(),
      lastPublishedAt: new Date().toISOString()
    } : wf));
  };
  // Handler for deleting a workflow
  const handleDeleteWorkflow = (workflowId: string) => {
    setWorkflows(workflows.filter(wf => wf.id !== workflowId));
  };
  // Handler for setting a workflow as default for its document type
  const handleSetAsDefault = (workflow: WorkflowDefinition) => {
    setWorkflows(workflows.map(wf => wf.documentType === workflow.documentType ? {
      ...wf,
      isDefault: wf.id === workflow.id,
      updatedAt: wf.id === workflow.id ? new Date().toISOString() : wf.updatedAt
    } : wf));
  };
  // Handler for saving a workflow
  const handleSaveWorkflow = (updatedWorkflow: WorkflowDefinition) => {
    if (updatedWorkflow.id) {
      // Update existing workflow
      setWorkflows(workflows.map(wf => wf.id === updatedWorkflow.id ? {
        ...updatedWorkflow,
        updatedAt: new Date().toISOString()
      } : wf));
    } else {
      // Create new workflow
      const newWorkflow = {
        ...updatedWorkflow,
        id: `wf-${new Date().getTime()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setWorkflows([...workflows, newWorkflow]);
    }
    setActiveView('list');
    setSelectedWorkflow(null);
  };
  // Filter workflows based on search term and document type filter
  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) || workflow.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDocumentType = filterDocumentType === 'all' || workflow.documentType === filterDocumentType;
    return matchesSearch && matchesDocumentType;
  });
  // Get document type name from ID
  const getDocumentTypeName = (id: string) => {
    const docType = documentTypes.find(dt => dt.id === id);
    return docType ? docType.name : id;
  };
  // Render the workflows list
  const renderWorkflowsList = () => {
    return <div className="space-y-4" data-id="element-10248">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-4 sm:space-y-0" data-id="element-10249">
          <div data-id="element-10250">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-10251">
              Workflow Definitions
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-10252">
              Manage business process workflows for different document types
            </p>
          </div>
          <button onClick={handleCreateWorkflow} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10253">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-10254" />
            Create Workflow
          </button>
        </div>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-4" data-id="element-10255">
          <div className="relative rounded-md shadow-sm flex-1" data-id="element-10256">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-10 py-2 sm:text-sm border-gray-300 rounded-md" placeholder="Search workflows..." data-id="element-10257" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-10258">
              <FileTextIcon className="h-4 w-4 text-gray-400" data-id="element-10259" />
            </div>
          </div>
          <div className="sm:w-64" data-id="element-10260">
            <select value={filterDocumentType} onChange={e => setFilterDocumentType(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10261">
              <option value="all" data-id="element-10262">All Document Types</option>
              {documentTypes.map(docType => <option key={docType.id} value={docType.id} data-id="element-10263">
                  {docType.name}
                </option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col" data-id="element-10264">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-10265">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-10266">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-10267">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-10268">
                  <thead className="bg-gray-50" data-id="element-10269">
                    <tr data-id="element-10270">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10271">
                        Workflow
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10272">
                        Document Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10273">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10274">
                        Last Updated
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10275">
                        Version
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10276">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10277">
                    {filteredWorkflows.map(workflow => <tr key={workflow.id} data-id="element-10278">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10279">
                          <div className="flex items-center" data-id="element-10280">
                            <div data-id="element-10281">
                              <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-10282">
                                {workflow.name}
                                {workflow.isDefault && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-10283">
                                    Default
                                  </span>}
                              </div>
                              <div className="text-xs text-gray-500 mt-1" data-id="element-10284">
                                {workflow.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10285">
                          <div className="text-sm text-gray-900" data-id="element-10286">
                            {getDocumentTypeName(workflow.documentType)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10287">
                          {workflow.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-10288" />}
                          {workflow.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-10289" />}
                          {workflow.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-10290" />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10291">
                          <div className="text-sm text-gray-900" data-id="element-10292">
                            {formatDate(workflow.updatedAt)}
                          </div>
                          {workflow.lastPublishedAt && <div className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-10293">
                              <ClockIcon className="h-3 w-3 mr-1" data-id="element-10294" />
                              Published: {formatDate(workflow.lastPublishedAt)}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10295">
                          <div className="text-sm text-gray-900" data-id="element-10296">
                            v{workflow.version}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10297">
                          <div className="flex justify-end space-x-2" data-id="element-10298">
                            <button onClick={() => handleEditWorkflow(workflow)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-10299">
                              <EditIcon className="h-4 w-4" data-id="element-10300" />
                            </button>
                            <button onClick={() => handlePreviewWorkflow(workflow)} className="text-blue-600 hover:text-blue-900" title="Preview" data-id="element-10301">
                              <PlayIcon className="h-4 w-4" data-id="element-10302" />
                            </button>
                            <button onClick={() => handleDuplicateWorkflow(workflow)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-10303">
                              <CopyIcon className="h-4 w-4" data-id="element-10304" />
                            </button>
                            {workflow.status === 'Draft' && <button onClick={() => handlePublishWorkflow(workflow.id)} className="text-green-600 hover:text-green-900" title="Publish" data-id="element-10305">
                                <CheckCircleIcon className="h-4 w-4" data-id="element-10306" />
                              </button>}
                            {!workflow.isDefault && workflow.status === 'Active' && <button onClick={() => handleSetAsDefault(workflow)} className="text-amber-600 hover:text-amber-900" title="Set as Default" data-id="element-10307">
                                  <LayersIcon className="h-4 w-4" data-id="element-10308" />
                                </button>}
                            {workflow.status !== 'Draft' && <button onClick={() => handleToggleWorkflowStatus(workflow.id)} className={`${workflow.status === 'Active' ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'}`} title={workflow.status === 'Active' ? 'Deactivate' : 'Activate'} data-id="element-10309">
                                {workflow.status === 'Active' ? <ToggleRightIcon className="h-4 w-4" data-id="element-10310" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-10311" />}
                              </button>}
                            <button onClick={() => handleDeleteWorkflow(workflow.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-10312">
                              <TrashIcon className="h-4 w-4" data-id="element-10313" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                {filteredWorkflows.length === 0 && <div className="text-center py-4 text-sm text-gray-500" data-id="element-10314">
                    No workflows found.{' '}
                    {workflows.length > 0 ? 'Try adjusting your filters.' : 'Click "Create Workflow" to create one.'}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the workflow editor
  const renderWorkflowEditor = () => {
    if (!selectedWorkflow) return null;
    return <div className="space-y-6" data-id="element-10315">
        <div className="flex justify-between items-center mb-4" data-id="element-10316">
          <button onClick={() => {
          setActiveView('list');
          setSelectedWorkflow(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10317">
            ← Back to Workflows
          </button>
          <div className="flex space-x-2" data-id="element-10318">
            <button onClick={() => handleSaveWorkflow(selectedWorkflow)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10319">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-10320" />
              Save
            </button>
          </div>
        </div>
        {/* Workflow details form */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10321">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10322">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10323">
              Workflow Details
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-10324">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-10325">
              <div className="sm:col-span-3" data-id="element-10326">
                <label htmlFor="workflow-name" className="block text-sm font-medium text-gray-700" data-id="element-10327">
                  Name
                </label>
                <div className="mt-1" data-id="element-10328">
                  <input type="text" id="workflow-name" value={selectedWorkflow.name} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  name: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10329" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-10330">
                <label htmlFor="document-type" className="block text-sm font-medium text-gray-700" data-id="element-10331">
                  Document Type
                </label>
                <div className="mt-1" data-id="element-10332">
                  <select id="document-type" value={selectedWorkflow.documentType} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  documentType: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10333">
                    <option value="" data-id="element-10334">Select Document Type</option>
                    {documentTypes.map(docType => <option key={docType.id} value={docType.id} data-id="element-10335">
                        {docType.name}
                      </option>)}
                  </select>
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-10336">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-10337">
                  Description
                </label>
                <div className="mt-1" data-id="element-10338">
                  <textarea id="description" rows={3} value={selectedWorkflow.description} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  description: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10339" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-10340">
                <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-10341">
                  Version
                </label>
                <div className="mt-1" data-id="element-10342">
                  <input type="text" id="version" value={selectedWorkflow.version} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  version: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10343" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-10344">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-10345">
                  Status
                </label>
                <div className="mt-1" data-id="element-10346">
                  <select id="status" value={selectedWorkflow.status} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  status: e.target.value as 'Active' | 'Draft' | 'Inactive'
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10347">
                    <option value="Draft" data-id="element-10348">Draft</option>
                    <option value="Active" data-id="element-10349">Active</option>
                    <option value="Inactive" data-id="element-10350">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-10351">
                <label htmlFor="default" className="block text-sm font-medium text-gray-700" data-id="element-10352">
                  Default Workflow
                </label>
                <div className="mt-1" data-id="element-10353">
                  <select id="default" value={selectedWorkflow.isDefault ? 'yes' : 'no'} onChange={e => setSelectedWorkflow({
                  ...selectedWorkflow,
                  isDefault: e.target.value === 'yes'
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10354">
                    <option value="no" data-id="element-10355">No</option>
                    <option value="yes" data-id="element-10356">Yes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* BPMN Diagram Editor */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10357">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10358">
            <div className="flex justify-between items-center" data-id="element-10359">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10360">
                Workflow Diagram
              </h3>
              <div className="flex space-x-2" data-id="element-10361">
                <button onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))} className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10362">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-10363" />
                </button>
                <span className="inline-flex items-center px-2 text-sm text-gray-500" data-id="element-10364">
                  {zoomLevel}%
                </span>
                <button onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))} className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10365">
                  <ZoomInIcon className="h-5 w-5" data-id="element-10366" />
                </button>
                <button onClick={() => setZoomLevel(100)} className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10367">
                  <RotateCwIcon className="h-5 w-5" data-id="element-10368" />
                </button>
              </div>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6 overflow-auto" data-id="element-10369">
            {/* BPMN Editor would go here - showing a placeholder for now */}
            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg text-center" data-id="element-10370">
              <div className="mx-auto h-12 w-12 text-gray-400" data-id="element-10371">
                <LayersIcon className="h-full w-full" data-id="element-10372" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-10373">
                BPMN Workflow Editor
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-10374">
                The BPMN editor would be integrated here to visually design the
                workflow
              </p>
              <div className="mt-6" data-id="element-10375">
                <div className="bg-gray-100 p-4 rounded-md" data-id="element-10376">
                  <div className="flex items-center justify-between mb-4" data-id="element-10377">
                    <div className="flex items-center" data-id="element-10378">
                      <BoxIcon className="h-6 w-6 text-gray-500 mr-2" data-id="element-10379" />
                      <span className="font-medium" data-id="element-10380">Start</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10381" />
                  </div>
                  <div className="flex items-center justify-between mb-4 ml-8" data-id="element-10382">
                    <div className="flex items-center" data-id="element-10383">
                      <FileTextIcon className="h-6 w-6 text-indigo-500 mr-2" data-id="element-10384" />
                      <span className="font-medium" data-id="element-10385">Document Validation</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10386" />
                  </div>
                  <div className="flex items-center justify-between mb-4 ml-8" data-id="element-10387">
                    <div className="flex items-center" data-id="element-10388">
                      <UserIcon className="h-6 w-6 text-blue-500 mr-2" data-id="element-10389" />
                      <span className="font-medium" data-id="element-10390">Manager Approval</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10391" />
                  </div>
                  <div className="flex items-center justify-between mb-4 ml-8" data-id="element-10392">
                    <div className="flex items-center" data-id="element-10393">
                      <UsersIcon className="h-6 w-6 text-green-500 mr-2" data-id="element-10394" />
                      <span className="font-medium" data-id="element-10395">Financial Approval</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10396" />
                  </div>
                  <div className="flex items-center ml-8" data-id="element-10397">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" data-id="element-10398" />
                    <span className="font-medium" data-id="element-10399">End</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Task Configuration */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10400">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10401">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10402">
              Task Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-10403">
              Configure individual tasks within the workflow
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-10404">
            <div className="space-y-4" data-id="element-10405">
              {/* Sample task configurations */}
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10406">
                <div className="flex items-start" data-id="element-10407">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10408">
                    <FileTextIcon className="h-5 w-5 text-indigo-500" data-id="element-10409" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10410">
                    <div className="flex items-center justify-between" data-id="element-10411">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-10412">
                        Document Validation
                      </h4>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10413">
                        System Task
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-500" data-id="element-10414">
                      Validates document data and required fields
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-10415">
                      <div className="sm:col-span-1" data-id="element-10416">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10417">
                          Timeout
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-10418">
                          <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" placeholder="30" data-id="element-10419" />
                          <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-10420">
                            minutes
                          </span>
                        </div>
                      </div>
                      <div className="sm:col-span-1" data-id="element-10421">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10422">
                          On Failure
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10423">
                          <option data-id="element-10424">Notify Admin</option>
                          <option data-id="element-10425">Retry</option>
                          <option data-id="element-10426">End Process</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10427">
                <div className="flex items-start" data-id="element-10428">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10429">
                    <UserIcon className="h-5 w-5 text-blue-500" data-id="element-10430" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10431">
                    <div className="flex items-center justify-between" data-id="element-10432">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-10433">
                        Manager Approval
                      </h4>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-10434">
                        User Task
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-500" data-id="element-10435">
                      Requires manager approval based on document value
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-10436">
                      <div className="sm:col-span-1" data-id="element-10437">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10438">
                          Assignee
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10439">
                          <option data-id="element-10440">Document Owner's Manager</option>
                          <option data-id="element-10441">Specific Role</option>
                          <option data-id="element-10442">Dynamic Assignment</option>
                        </select>
                      </div>
                      <div className="sm:col-span-1" data-id="element-10443">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10444">
                          Due Date
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-10445">
                          <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" placeholder="2" data-id="element-10446" />
                          <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-10447">
                            business days
                          </span>
                        </div>
                      </div>
                      <div className="sm:col-span-2" data-id="element-10448">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10449">
                          Escalation
                        </label>
                        <div className="mt-1 flex items-center" data-id="element-10450">
                          <input id="escalation" name="escalation" type="checkbox" className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-10451" />
                          <label htmlFor="escalation" className="ml-2 block text-sm text-gray-900" data-id="element-10452">
                            Enable escalation after
                          </label>
                          <div className="ml-2 flex rounded-md shadow-sm w-20" data-id="element-10453">
                            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-md sm:text-sm border-gray-300" placeholder="24" data-id="element-10454" />
                          </div>
                          <span className="ml-2 text-sm text-gray-500" data-id="element-10455">
                            hours
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10456">
                <div className="flex items-start" data-id="element-10457">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10458">
                    <UsersIcon className="h-5 w-5 text-green-500" data-id="element-10459" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10460">
                    <div className="flex items-center justify-between" data-id="element-10461">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-10462">
                        Financial Approval
                      </h4>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-10463">
                        User Task
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-500" data-id="element-10464">
                      Financial approval based on delegation thresholds
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-10465">
                      <div className="sm:col-span-1" data-id="element-10466">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10467">
                          Delegation Reference
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10468">
                          <option data-id="element-10469">Financial Delegation Matrix</option>
                          <option data-id="element-10470">Custom Assignment</option>
                        </select>
                      </div>
                      <div className="sm:col-span-1" data-id="element-10471">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10472">
                          Due Date
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-10473">
                          <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" placeholder="3" data-id="element-10474" />
                          <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-10475">
                            business days
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Add Task Button */}
              <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10476">
                <PlusIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" data-id="element-10477" />
                Add Task
              </button>
            </div>
          </div>
        </div>
        {/* Notifications Configuration */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10478">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10479">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10480">
              Notifications
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-10481">
              Configure notifications for this workflow
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-10482">
            <div className="space-y-4" data-id="element-10483">
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10484">
                <div className="flex items-start" data-id="element-10485">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10486">
                    <BellIcon className="h-5 w-5 text-amber-500" data-id="element-10487" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10488">
                    <div className="flex items-center justify-between" data-id="element-10489">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-10490">
                        Task Assignment
                      </h4>
                      <button type="button" className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10491">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-10492" />
                      </button>
                    </div>
                    <div className="mt-2 text-sm text-gray-500" data-id="element-10493">
                      Notify users when a task is assigned to them
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-10494">
                      <div className="sm:col-span-3" data-id="element-10495">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10496">
                          Channels
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-10497">
                          <div className="flex items-center" data-id="element-10498">
                            <input id="email-channel" name="email-channel" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10499" />
                            <label htmlFor="email-channel" className="ml-2 block text-sm text-gray-900" data-id="element-10500">
                              Email
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-10501">
                            <input id="teams-channel" name="teams-channel" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10502" />
                            <label htmlFor="teams-channel" className="ml-2 block text-sm text-gray-900" data-id="element-10503">
                              Microsoft Teams
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-10504">
                            <input id="inapp-channel" name="inapp-channel" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10505" />
                            <label htmlFor="inapp-channel" className="ml-2 block text-sm text-gray-900" data-id="element-10506">
                              In-App
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-10507">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10508">
                          Template
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10509">
                          <option data-id="element-10510">Task Assignment</option>
                          <option data-id="element-10511">Custom Template</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10512">
                <div className="flex items-start" data-id="element-10513">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10514">
                    <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-10515" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10516">
                    <div className="flex items-center justify-between" data-id="element-10517">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-10518">
                        Task Escalation
                      </h4>
                      <button type="button" className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10519">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-10520" />
                      </button>
                    </div>
                    <div className="mt-2 text-sm text-gray-500" data-id="element-10521">
                      Notify users when a task is escalated
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-10522">
                      <div className="sm:col-span-3" data-id="element-10523">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10524">
                          Recipients
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-10525">
                          <div className="flex items-center" data-id="element-10526">
                            <input id="assignee" name="assignee" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10527" />
                            <label htmlFor="assignee" className="ml-2 block text-sm text-gray-900" data-id="element-10528">
                              Original Assignee
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-10529">
                            <input id="manager" name="manager" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10530" />
                            <label htmlFor="manager" className="ml-2 block text-sm text-gray-900" data-id="element-10531">
                              Assignee's Manager
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-10532">
                            <input id="admin" name="admin" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10533" />
                            <label htmlFor="admin" className="ml-2 block text-sm text-gray-900" data-id="element-10534">
                              System Administrator
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-10535">
                        <label className="block text-xs font-medium text-gray-500" data-id="element-10536">
                          Channels
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-10537">
                          <div className="flex items-center" data-id="element-10538">
                            <input id="email-channel-esc" name="email-channel-esc" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10539" />
                            <label htmlFor="email-channel-esc" className="ml-2 block text-sm text-gray-900" data-id="element-10540">
                              Email
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-10541">
                            <input id="teams-channel-esc" name="teams-channel-esc" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10542" />
                            <label htmlFor="teams-channel-esc" className="ml-2 block text-sm text-gray-900" data-id="element-10543">
                              Microsoft Teams
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-10544">
                            <input id="inapp-channel-esc" name="inapp-channel-esc" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked data-id="element-10545" />
                            <label htmlFor="inapp-channel-esc" className="ml-2 block text-sm text-gray-900" data-id="element-10546">
                              In-App
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Add Notification Button */}
              <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10547">
                <PlusIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" data-id="element-10548" />
                Add Notification
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the workflow preview
  const renderWorkflowPreview = () => {
    if (!selectedWorkflow) return null;
    return <div className="space-y-6" data-id="element-10549">
        <div className="flex justify-between items-center mb-4" data-id="element-10550">
          <button onClick={() => {
          setActiveView('list');
          setSelectedWorkflow(null);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10551">
            ← Back to Workflows
          </button>
          <div className="flex space-x-2" data-id="element-10552">
            <button onClick={() => handleEditWorkflow(selectedWorkflow)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10553">
              <EditIcon className="h-4 w-4 mr-2" data-id="element-10554" />
              Edit
            </button>
            <button onClick={() => {}} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10555">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-10556" />
              Export
            </button>
          </div>
        </div>
        {/* Workflow details */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10557">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10558">
            <div className="flex justify-between items-center" data-id="element-10559">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10560">
                {selectedWorkflow.name}
              </h3>
              <StatusBadge status={selectedWorkflow.status === 'Active' ? 'success' : selectedWorkflow.status === 'Draft' ? 'info' : 'error'} text={selectedWorkflow.status} data-id="element-10561" />
            </div>
            <p className="mt-1 text-sm text-gray-500" data-id="element-10562">
              {selectedWorkflow.description}
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0" data-id="element-10563">
            <dl className="sm:divide-y sm:divide-gray-200" data-id="element-10564">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-10565">
                <dt className="text-sm font-medium text-gray-500" data-id="element-10566">
                  Document Type
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-10567">
                  {getDocumentTypeName(selectedWorkflow.documentType)}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-10568">
                <dt className="text-sm font-medium text-gray-500" data-id="element-10569">Version</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-10570">
                  v{selectedWorkflow.version}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-10571">
                <dt className="text-sm font-medium text-gray-500" data-id="element-10572">
                  Last Updated
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-10573">
                  {formatDate(selectedWorkflow.updatedAt)}
                </dd>
              </div>
              {selectedWorkflow.lastPublishedAt && <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-10574">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-10575">
                    Last Published
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-10576">
                    {formatDate(selectedWorkflow.lastPublishedAt)}
                  </dd>
                </div>}
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-10577">
                <dt className="text-sm font-medium text-gray-500" data-id="element-10578">
                  Default Workflow
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-10579">
                  {selectedWorkflow.isDefault ? 'Yes' : 'No'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        {/* BPMN Diagram Preview */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10580">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10581">
            <div className="flex justify-between items-center" data-id="element-10582">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10583">
                Workflow Diagram
              </h3>
              <div className="flex space-x-2" data-id="element-10584">
                <button onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))} className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10585">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-10586" />
                </button>
                <span className="inline-flex items-center px-2 text-sm text-gray-500" data-id="element-10587">
                  {zoomLevel}%
                </span>
                <button onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))} className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10588">
                  <ZoomInIcon className="h-5 w-5" data-id="element-10589" />
                </button>
                <button onClick={() => setZoomLevel(100)} className="inline-flex items-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10590">
                  <RotateCwIcon className="h-5 w-5" data-id="element-10591" />
                </button>
              </div>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6 overflow-auto" data-id="element-10592">
            {/* BPMN Viewer would go here - showing a placeholder for now */}
            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg text-center" data-id="element-10593">
              <div className="mx-auto h-12 w-12 text-gray-400" data-id="element-10594">
                <LayersIcon className="h-full w-full" data-id="element-10595" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-10596">
                BPMN Workflow Viewer
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-10597">
                The BPMN viewer would be integrated here to visualize the
                workflow
              </p>
              <div className="mt-6" data-id="element-10598">
                <div className="bg-gray-100 p-4 rounded-md" data-id="element-10599">
                  <div className="flex items-center justify-between mb-4" data-id="element-10600">
                    <div className="flex items-center" data-id="element-10601">
                      <BoxIcon className="h-6 w-6 text-gray-500 mr-2" data-id="element-10602" />
                      <span className="font-medium" data-id="element-10603">Start</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10604" />
                  </div>
                  <div className="flex items-center justify-between mb-4 ml-8" data-id="element-10605">
                    <div className="flex items-center" data-id="element-10606">
                      <FileTextIcon className="h-6 w-6 text-indigo-500 mr-2" data-id="element-10607" />
                      <span className="font-medium" data-id="element-10608">Document Validation</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10609" />
                  </div>
                  <div className="flex items-center justify-between mb-4 ml-8" data-id="element-10610">
                    <div className="flex items-center" data-id="element-10611">
                      <UserIcon className="h-6 w-6 text-blue-500 mr-2" data-id="element-10612" />
                      <span className="font-medium" data-id="element-10613">Manager Approval</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10614" />
                  </div>
                  <div className="flex items-center justify-between mb-4 ml-8" data-id="element-10615">
                    <div className="flex items-center" data-id="element-10616">
                      <UsersIcon className="h-6 w-6 text-green-500 mr-2" data-id="element-10617" />
                      <span className="font-medium" data-id="element-10618">Financial Approval</span>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-500" data-id="element-10619" />
                  </div>
                  <div className="flex items-center ml-8" data-id="element-10620">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" data-id="element-10621" />
                    <span className="font-medium" data-id="element-10622">End</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Task Details */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10623">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10624">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10625">
              Task Details
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-10626">
            <div className="space-y-4" data-id="element-10627">
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10628">
                <div className="flex items-start" data-id="element-10629">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10630">
                    <FileTextIcon className="h-5 w-5 text-indigo-500" data-id="element-10631" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10632">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-10633">
                      Document Validation
                    </h4>
                    <div className="mt-1 text-sm text-gray-500" data-id="element-10634">
                      <p data-id="element-10635">
                        System task that validates document data and required
                        fields
                      </p>
                      <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6" data-id="element-10636">
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10637">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10638" />
                          Timeout: 30 minutes
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10639">
                          <AlertCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10640" />
                          On Failure: Notify Admin
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10641">
                <div className="flex items-start" data-id="element-10642">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10643">
                    <UserIcon className="h-5 w-5 text-blue-500" data-id="element-10644" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10645">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-10646">
                      Manager Approval
                    </h4>
                    <div className="mt-1 text-sm text-gray-500" data-id="element-10647">
                      <p data-id="element-10648">Requires manager approval based on document value</p>
                      <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6" data-id="element-10649">
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10650">
                          <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10651" />
                          Assignee: Document Owner's Manager
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10652">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10653" />
                          Due Date: 2 business days
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10654">
                          <AlertCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10655" />
                          Escalation: After 24 hours
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-10656">
                <div className="flex items-start" data-id="element-10657">
                  <div className="flex-shrink-0 mt-0.5" data-id="element-10658">
                    <UsersIcon className="h-5 w-5 text-green-500" data-id="element-10659" />
                  </div>
                  <div className="ml-3 flex-1" data-id="element-10660">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-10661">
                      Financial Approval
                    </h4>
                    <div className="mt-1 text-sm text-gray-500" data-id="element-10662">
                      <p data-id="element-10663">Financial approval based on delegation thresholds</p>
                      <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6" data-id="element-10664">
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10665">
                          <UsersIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10666" />
                          Delegation: Financial Delegation Matrix
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-10667">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-10668" />
                          Due Date: 3 business days
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Main render function
  return <div className="space-y-6" data-id="element-10669">
      <div className="mb-8" data-id="element-10670">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-10671">Workflows</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10672">
          Design and manage business process workflows with BPMN visualization
        </p>
      </div>
      {/* Main Content */}
      {activeView === 'list' ? renderWorkflowsList() : activeView === 'edit' ? renderWorkflowEditor() : renderWorkflowPreview()}
    </div>;
}